import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useRef, useState } from "react";
import { useDashboardRouter as useRouter } from "@/hooks/useDashboardRouter";
import type { WorkspaceArtifactId } from "../types";

/** The visible artifact lives on the URL, e.g. `/sessions/abc?pane=<artifact id>`. */
export const WORKSPACE_PANE_QUERY_PARAM = "pane";
export function workspacePaneIdFromQuery(value: string | string[] | undefined): WorkspaceArtifactId | null {
  const raw = Array.isArray(value) ? value[0] : value;
  return raw === undefined || raw === "" ? null : raw;
}
interface PendingPane {
  id: WorkspaceArtifactId | null;
  url: string;
  sequence: number;
}
function pathnameOf(url: string): string {
  return url.split(/[?#]/, 1)[0];
}
export interface WorkspacePaneUrl {
  paneId: WorkspaceArtifactId | null;
  setPaneId: (id: WorkspaceArtifactId | null, options?: {
    replace?: boolean;
  }) => void;
}

/** URL-backed pane id with an optimistic value while Next's shallow route settles. */
export function useWorkspacePaneUrl() {
  const $ = _c(10);
  const router = useRouter();
  const [localId, setLocalId] = useState(null);
  const [pending, setPending] = useState();
  const sequence = useRef(0);
  let t0;
  if ($[0] !== router) {
    t0 = router ? workspacePaneIdFromQuery(router.query[WORKSPACE_PANE_QUERY_PARAM]) : null;
    $[0] = router;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const routeId = t0;
  const routePathname = router ? pathnameOf(router.asPath) : null;
  const pendingSettled = pending !== undefined && (routeId === pending.id || routePathname !== null && routePathname !== pathnameOf(pending.url));
  if (pendingSettled) {
    setPending(undefined);
  }
  const activePending = pendingSettled ? undefined : pending;
  let t1;
  let t2;
  if ($[2] !== router) {
    t1 = () => {
      if (!router?.events) {
        return;
      }
      const settle = () => setPending(undefined);
      const settleUrl = url => {
        setPending(current => current?.url === url ? undefined : current);
      };
      const settleError = (_error, url_0) => settleUrl(url_0);
      router.events.on("routeChangeComplete", settleUrl);
      router.events.on("routeChangeError", settleError);
      window.addEventListener("popstate", settle);
      return () => {
        router.events.off("routeChangeComplete", settleUrl);
        router.events.off("routeChangeError", settleError);
        window.removeEventListener("popstate", settle);
      };
    };
    t2 = [router];
    $[2] = router;
    $[3] = t1;
    $[4] = t2;
  } else {
    t1 = $[3];
    t2 = $[4];
  }
  useEffect(t1, t2);
  const paneId = router ? activePending ? activePending.id : routeId : localId;
  let t3;
  if ($[5] !== router) {
    t3 = (next, options) => {
      if (!router) {
        setLocalId(next);
        return;
      }
      const params = new URLSearchParams(window.location.search);
      const openInUrl = params.has(WORKSPACE_PANE_QUERY_PARAM);
      if (next === null) {
        params.delete(WORKSPACE_PANE_QUERY_PARAM);
      } else {
        params.set(WORKSPACE_PANE_QUERY_PARAM, next);
      }
      const query = params.toString();
      const url_1 = `${window.location.pathname}${query ? `?${query}` : ""}${window.location.hash}`;
      const nextSequence = sequence.current = sequence.current + 1;
      setPending({
        id: next,
        url: url_1,
        sequence: nextSequence
      });
      const navigate = !openInUrl && next !== null && !options?.replace ? router.push(url_1, undefined, {
        shallow: true,
        scroll: false
      }) : router.replace(url_1, undefined, {
        shallow: true,
        scroll: false
      });
      const abandon = () => setPending(current_0 => current_0?.sequence === nextSequence ? undefined : current_0);
      Promise.resolve(navigate).then(completed => {
        if (!completed) {
          abandon();
        }
      }).catch(abandon);
    };
    $[5] = router;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const setPaneId = t3;
  let t4;
  if ($[7] !== paneId || $[8] !== setPaneId) {
    t4 = {
      paneId,
      setPaneId
    };
    $[7] = paneId;
    $[8] = setPaneId;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return t4;
}